import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ContentEditor = ({ content, onContentChange }) => {
  const [editedContent, setEditedContent] = useState(content);
  const [wordCount, setWordCount] = useState(0);
  const [isChecking, setIsChecking] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    setEditedContent(content);
    updateWordCount(content);
  }, [content]);

  const updateWordCount = (text) => {
    const words = text?.trim()?.split(/\s+/)?.filter(word => word?.length > 0);
    setWordCount(words?.length);
  };

  const handleContentChange = (e) => {
    const newContent = e?.target?.value;
    setEditedContent(newContent);
    updateWordCount(newContent);
    onContentChange(newContent);
  };

  const handleGrammarCheck = () => {
    setIsChecking(true);
    setTimeout(() => {
      setSuggestions([
        { type: 'grammar', text: 'Consider using active voice', position: 45 },
        { type: 'style', text: 'Replace jargon for clarity', position: 120 },
        { type: 'compliance', text: 'Add regulatory reference', position: 200 },
      ]);
      setIsChecking(false);
    }, 1500);
  };

  const formatOptions = [
    { id: 'bold', icon: 'Bold', label: 'Bold' },
    { id: 'italic', icon: 'Italic', label: 'Italic' },
    { id: 'underline', icon: 'Underline', label: 'Underline' },
    { id: 'list', icon: 'List', label: 'Bullet List' },
    { id: 'numbered', icon: 'ListOrdered', label: 'Numbered List' },
    { id: 'heading', icon: 'Heading', label: 'Heading' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Content Editor</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGrammarCheck}
            disabled={isChecking}
            className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors text-sm disabled:opacity-50"
          >
            <Icon name={isChecking ? 'Loader' : 'CheckCircle'} size={16} className={isChecking ? 'animate-spin' : ''} />
            {isChecking ? 'Checking...' : 'Check Grammar'}
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm">
            <Icon name="Save" size={16} />
            Save Draft
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="flex items-center gap-1 p-2 border-b border-border bg-muted/30">
          {formatOptions?.map((option) => (
            <button
              key={option?.id}
              className="p-2 hover:bg-muted rounded transition-colors"
              title={option?.label}
            >
              <Icon name={option?.icon} size={18} />
            </button>
          ))}
          <div className="w-px h-6 bg-border mx-2" />
          <button className="p-2 hover:bg-muted rounded transition-colors" title="Undo">
            <Icon name="Undo" size={18} />
          </button>
          <button className="p-2 hover:bg-muted rounded transition-colors" title="Redo">
            <Icon name="Redo" size={18} />
          </button>
        </div>

        <textarea
          value={editedContent}
          onChange={handleContentChange}
          placeholder="Start editing your generated content here..."
          className="w-full px-6 py-4 bg-background border-0 focus:ring-0 resize-none text-foreground font-mono text-sm leading-relaxed"
          rows={20}
          style={{ minHeight: '500px' }}
        />

        <div className="flex items-center justify-between px-6 py-3 border-t border-border bg-muted/30">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{wordCount} words</span>
            <span>|</span>
            <span>{editedContent?.length} characters</span>
            <span>|</span>
            <span>Last saved: Just now</span>
          </div>
          <div className="flex items-center gap-2">
            <button className="text-xs text-primary hover:text-primary/80">
              View Formatting Guide
            </button>
          </div>
        </div>
      </div>

      {suggestions?.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertCircle" size={18} color="var(--color-primary)" />
            <h4 className="font-semibold text-foreground">Suggestions</h4>
          </div>
          <div className="space-y-2">
            {suggestions?.map((suggestion, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                <Icon
                  name={suggestion?.type === 'grammar' ? 'CheckCircle' : suggestion?.type === 'style' ? 'Sparkles' : 'Shield'}
                  size={16}
                  color="var(--color-primary)"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm text-foreground">{suggestion?.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">Position: {suggestion?.position}</p>
                </div>
                <button className="text-xs text-primary hover:text-primary/80">
                  Apply
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentEditor;